import { useMemo } from 'react';
import { collection, query, where, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useFirestoreCollectionOnce } from './useFirestoreCollectionOnce';
import { Collections, type EventLocation } from '@event-planner/shared';

/**
 * Admin-side subscription to all event locations for an event. Used by
 * the LocationPicker to surface existing locations in its toggle-on
 * autocomplete dropdown, and by the future Map admin tab.
 *
 * One-time fetch with refetch (matches the rest of the admin's data
 * fetching pattern per ADR-015 / ADR-019). Returns the collection
 * sorted by label for consistent ordering across consumers.
 */
export function useEventLocations(eventId: string): {
  locations: EventLocation[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const locationsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, Collections.EVENT_LOCATIONS),
            where('eventId', '==', eventId),
            orderBy('label', 'asc'),
          )
        : null,
    [eventId],
  );

  const { data, loading, error, refetch } =
    useFirestoreCollectionOnce<EventLocation>(locationsQuery);

  return { locations: data, isLoading: loading, error, refetch };
}
